import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getUserData } from "../utils/auth-helpers";
import { getActiveAccountId, isGlobalPath } from "../utils/account-context";

export const resolveAccountName = (accountId, availableAccounts) => {
  const userData = getUserData();
  if (String(userData?.account?.id) === String(accountId)) {
    return userData?.account?.name || "";
  }
  const fromList = (availableAccounts || []).find((item) => String(item?.id) === String(accountId));
  if (fromList?.name) {
    return fromList.name;
  }
  return "";
};

export const useActiveAccount = ({ availableAccounts = [] } = {}) => {
  const location = useLocation();
  const allowStoredFallback = /\/edit-draft-campaigns\//.test(location.pathname);
  const activeAccountId = getActiveAccountId(location.pathname, { allowStoredFallback });

  useEffect(() => {
    // Never persist path segments from global auth routes (e.g. "auth" from /auth/google/callback).
    if (activeAccountId && !isGlobalPath(location.pathname)) {
      localStorage.setItem("accountId", activeAccountId);
    }
  }, [activeAccountId, location.pathname]);

  const isValidAccount = useMemo(() => {
    if (!activeAccountId || !availableAccounts.length) {
      return true;
    }
    return availableAccounts.some((account) => String(account?.id) === String(activeAccountId));
  }, [activeAccountId, availableAccounts]);

  const fallbackAccountId = useMemo(() => {
    if (isValidAccount || !availableAccounts.length) {
      return null;
    }
    return availableAccounts[0]?.id || null;
  }, [availableAccounts, isValidAccount]);

  const accountName = resolveAccountName(activeAccountId, availableAccounts);

  return {
    activeAccountId,
    accountName,
    isValidAccount,
    fallbackAccountId,
    pathname: location.pathname,
    search: location.search || "",
  };
};

export default useActiveAccount;
